import { Injectable } from '@angular/core';
import { NFIdentity } from '@nibras/core/common';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { CacheService } from './cache.service';
import { Caller, HttpService } from './http.service';
import { AppConstants } from './model';
import { ElementsUserAccessRequest, ElementsUserRoleAccess } from './model/access-security.model';
import { dcSearchTags } from './model/dcSearchTags.model';
import { dcTag } from './model/dcTag.model';
import { Dictionary } from './model/dictionary';
import { IdcActionInfo } from './model/response/dcActionInfo.model';
import {
  dcElementSecurityContext,
  dcElementSecurityInfo,
  dcRole,
  dcRoleAssignee,
  dcRoleAssignment,
  dcRoleAssignmentHistory,
  dcRoleSecurityUser,
  dcSecurityIdentity,
  dcTagSet,
  dcTransferRoleAssignment
} from './model/security/dcSecurityIdentity.model';
import { RoleAssignment } from './model/user.model';
import { TagService } from './tag.service';

@Injectable()
export class SecurityService {
  readonly apiRoot = 'Role/';
  readonly endPoints = {
    USER_ROLE: `${this.apiRoot}UserRole`,
    ROLE_ASSIGNMENT: `${this.apiRoot}RoleAssignment`,
    TAGSET_MAP: `${this.apiRoot}TagSetMap`,
    PREVIEW_SCOPE: `${this.apiRoot}PreviewScope`,
    SAVE_USER_ROLE_MAP: `${this.apiRoot}SaveUserRoleMap`,
    SAVE_ASSIGNMENT: `${this.apiRoot}SaveRoleAssignment`,
    TRANSFERASSIGNMENT: `${this.apiRoot}TransferAssignment`,
    USER_LOOKUP: `${this.apiRoot}GetUserDetails`,
    SAVE_SECURITY: `${this.apiRoot}SaveSecurityIdentity`,
    ADD_MEMBER: `${this.apiRoot}AddGroupMember`,
    REMOVE_MEMBER: `${this.apiRoot}RemoveGroupMember`,
    ROLE_ASSIGNMENT_FOR_ELEMENT: `${this.apiRoot}RoleAssignmentForElement`,
    ASSET_HIERARCHY: `${this.apiRoot}GetAssetHierarchy`,
    ASSIGNMENT_HISTORY: `${this.apiRoot}Assignment/History`,
    USER_ASSIGNMENT: `${this.apiRoot}UserAssignment`,
    ROLE_ASSIGNEE: `${this.apiRoot}RoleAssignee`,
    SWITCH_ROLE: `${this.apiRoot}SwitchRole`,
    ELEMENT_ROLE_ASSIGNMENT: `${this.apiRoot}GetElementRoleAssignment`,
    CREATE_NEW_ROLE: `${this.apiRoot}Save`
  };

  private readonly ROLE_ASSIGNMENT_FOR_ELEMENT_CACHE_KEY = 'ElementUserRoles';

  constructor(
    private httpService: HttpService,
    private authService: AuthService,
    private cacheService: CacheService,
    private tagService: TagService
  ) {}

  getSecurityDetails(securityId: number, cancelToken?: Caller): Observable<dcSecurityIdentity> {
    return this.httpService.get(`${this.endPoints.USER_ROLE}/${securityId}`, null, cancelToken);
  }

  getRoleDetails(roleId: number, securityId: number): Observable<dcRole> {
    return this.httpService.get(`${this.endPoints.ROLE_ASSIGNMENT}/${securityId}/${roleId}`);
  }

  getRoleAssignment(
    elementSecContext: dcElementSecurityContext
  ): Observable<dcElementSecurityContext> {
    return this.httpService.post(`${this.endPoints.ELEMENT_ROLE_ASSIGNMENT}`, elementSecContext);
  }

  deleteUserRole(roleId: number, securityId: number): Observable<IdcActionInfo> {
    return this.httpService.delete(`${this.endPoints.ROLE_ASSIGNMENT}/${securityId}/${roleId}`);
  }

  getRoleTagSetMap(tagSetMapId: number): Observable<dcTagSet> {
    return this.httpService.get(`${this.endPoints.TAGSET_MAP}/${tagSetMapId}/`);
  }

  previewScope(tagSet: dcTagSet): Observable<Dictionary<string[]>> {
    return this.httpService.post(this.endPoints.PREVIEW_SCOPE, tagSet);
  }

  saveUserRole(security: dcSecurityIdentity): Observable<IdcActionInfo> {
    return this.httpService.post(this.endPoints.SAVE_USER_ROLE_MAP, security);
  }

  userLookup(name: string, isGroup: boolean): Observable<dcSecurityIdentity> {
    return this.httpService.get(`${this.endPoints.USER_LOOKUP}/${0}/${isGroup}?loginID=${name}`);
  }

  userLookupWithSecurityID(
    securityid: number,
    name: string,
    isGroup: boolean
  ): Observable<dcSecurityIdentity> {
    return this.httpService.get(
      `${this.endPoints.USER_LOOKUP}/${securityid}/${isGroup}?loginID=${name}`
    );
  }

  getAssetHierarchy(): Observable<dcSecurityIdentity> {
    return this.httpService.get(`${this.endPoints.ASSET_HIERARCHY}/-1`);
  }

  saveRoleAssignments(
    scopes: dcRoleAssignment[]
  ): Observable<{ action: IdcActionInfo; roleassignments: dcRoleAssignment[] }> {
    return this.httpService.post(this.endPoints.SAVE_ASSIGNMENT, scopes);
  }

  transferScope(
    transferInput: dcTransferRoleAssignment
  ): Observable<{ action: IdcActionInfo; roleassignments: dcRoleAssignment[] }> {
    return this.httpService.post(this.endPoints.TRANSFERASSIGNMENT, transferInput);
  }

  saveSecurity(security: dcSecurityIdentity): Observable<IdcActionInfo> {
    return this.httpService.post(this.endPoints.SAVE_SECURITY, security);
  }

  addGroupMembers(security: dcSecurityIdentity): Observable<IdcActionInfo> {
    return this.httpService.post(this.endPoints.ADD_MEMBER, security);
  }

  removeGroupMember(security: dcSecurityIdentity): Observable<IdcActionInfo> {
    return this.httpService.post(this.endPoints.REMOVE_MEMBER, security);
  }

  public getElementUserRoles(
    elementVersionID: number,
    cancelToken?: Caller
  ): Observable<dcElementSecurityInfo> {
    return this.cacheService.get(
      this.ROLE_ASSIGNMENT_FOR_ELEMENT_CACHE_KEY + elementVersionID,
      this.httpService.get(
        `${this.endPoints.ROLE_ASSIGNMENT_FOR_ELEMENT}/${elementVersionID}`,
        null,
        cancelToken
      ),
      8 * 60 * 60 * 1000 // 8 hours
    );
  }

  getAssignmentHistory(
    elementVersionID: number,
    roleId: number
  ): Observable<dcRoleAssignmentHistory[]> {
    return this.httpService.post(`${this.endPoints.ASSIGNMENT_HISTORY}`, {
      ElementVersionID: elementVersionID,
      RoleID: roleId
    });
  }

  saveUserAssignment(
    roleUserAssignment: dcElementSecurityInfo
  ): Observable<{ action: IdcActionInfo; roleSecurityUser: dcRoleSecurityUser }> {
    return this.httpService.post(`${this.endPoints.USER_ASSIGNMENT}`, roleUserAssignment);
  }

  getRoleAssignees(roleId: number): Observable<dcRoleAssignee[]> {
    return this.httpService.get(`${this.endPoints.ROLE_ASSIGNEE}/${roleId}`);
  }

  createRole(role: dcRole): Observable<IdcActionInfo> {
    return this.httpService.post(`${this.endPoints.CREATE_NEW_ROLE}`, role);
  }

  updateRole(role: dcRole): Observable<IdcActionInfo> {
    return this.httpService.put(`${this.apiRoot}`, role);
  }

  getElementSupportedTypeTag() {
    const tag = new dcTag();
    tag.TagName = NFIdentity[NFIdentity.Element]; //'BT_BASE_ELEMENT';
    tag.ElementTypeID = NFIdentity.Element;
    return tag;
  }

  async getAllAppTags() {
    const inputParam = new dcSearchTags();
    const inputTag = new dcTag();
    inputTag.ElementTypeID = NFIdentity.Element;
    inputTag.TagName = 'BT_APP';
    inputParam.Filter = [inputTag];
    inputParam.IsElement = true;
    inputParam.IsGroup = true;
    const apps = await firstValueFrom(this.tagService.AdvancedSearch(inputParam)).catch(
      e => [] as dcTag[]
    );
    return apps;
  }

  async getAllProcessTags() {
    const inputParam = new dcSearchTags();
    const inputTag = new dcTag();
    inputTag.ElementTypeID = NFIdentity.ProcessDesign;
    inputTag.TagName = 'ACTIONABLE_WORKFLOW';
    inputParam.Filter = [inputTag];
    inputParam.IsElement = true;
    inputParam.IsGroup = true;
    const process = await firstValueFrom(this.tagService.AdvancedSearch(inputParam)).catch(
      e => [] as dcTag[]
    );
    return process;
  }

  public getRolesWithAppAccess(roleAssignments: RoleAssignment[], appId: number) {
    return roleAssignments.filter(roleAssignment => this.hasAppAccess(roleAssignment, appId));
  }

  public hasAppAccess(roleAssignment: RoleAssignment, appId: number): boolean {
    // If any static product level app eg. AppStore, return true
    if (roleAssignment.Role && AppConstants.IGNORE_SECURITYCHECK_APPS.includes(appId)) {
      return true;
    }

    return roleAssignment.Role.Tags.some(
      tag => tag.ElementTypeID === NFIdentity.App && tag.SourceObjectID === appId
    );
  }

  switchRole(securityId: number, roleId: number): Observable<boolean> {
    return this.httpService.post(`${this.endPoints.SWITCH_ROLE}`, {
      SecurityId: securityId,
      ID: roleId
    });
  }

  getElementsUserRoleAccess(
    inpData: ElementsUserAccessRequest
  ): Observable<ElementsUserRoleAccess[]> {
    const cacheKey = this.userAccessKey(inpData);
    return this.httpService.post<ElementsUserRoleAccess[]>(
      'Role/GetElementsUserRoleAccess',
      inpData,
      cacheKey,
      cacheKey
    );
  }

  /** Check user persmission to add edit and delete record.
   * This API returns so much data. If performance issue there then we will change this API
   */
  public async hasAccess(elementVersionId: number) {
    const userID = this.authService.userElementVersionId;
    const response = await firstValueFrom(this.getElementUserRoles(elementVersionId)).catch(
      () => null as dcElementSecurityInfo
    );
    if (response && response.Assignments && response.Assignments.length) {
      const data = response.Assignments.find(
        x => x.Identities && x.Identities.findIndex(y => y.SecurityID == userID) > -1
      );
      if (data && data.Identities.length > 0) return true;
    }
    return false;
  }

  private userAccessKey(data: ElementsUserAccessRequest) {
    const roleKey = data?.ElementRoleList?.map(r => `${r.RoleID}-${r.RootElementVersionID}`)?.join(
      '-'
    );
    return `${data?.SecurityID}-${data?.CategoryID}-${roleKey}`;
  }
}
